import type { RecipeJsonLdProps } from 'next-seo';

import { APP_ROUTES } from 'constants/routes';
import { minutesToISODuration } from 'helpers/DateHelpers';
import { getYoutubeVideoIdFromUrl } from 'helpers/VideoHelpers';

import {
  DEFAULT_SEO_IMAGE, getOpenGraphUrl, getPageSeoBreadcrumbs, getPaginationCanonical
} from './SeoHelpers';

import type {
  IRecipe, IRecipesByTagPage, IRecipesLandingPage, IRecipesListPageUrlParams, IRecipesPage, PageSeoProps, TFunction
} from 'types';

/* Recipes */

// Recipes landing page

export function getRecipesLandingPageSeo(t: TFunction, recipesLandingPage?: IRecipesLandingPage): PageSeoProps {
  if (!recipesLandingPage) return { seoProps: {}, breadcrumbs: [] };
  const {
    seo
  } = recipesLandingPage || {};

  const {
    title,
    description,
    images
  } = seo || {};
  return {
    seoProps: {
      title,
      description,
      noindex: false,
      openGraph: {
        url: getOpenGraphUrl(),
        description,
        images: images || DEFAULT_SEO_IMAGE
      }
    },
    breadcrumbs: getPageSeoBreadcrumbs(t)
  };
}

// Recipes by tag
export function getRecipesByTagPageSeo(t: TFunction, recipesByTagPage?: IRecipesByTagPage): PageSeoProps {
  if (!recipesByTagPage) return { seoProps: {}, breadcrumbs: [] };
  const {
    title,
    images
  } = recipesByTagPage.seo || {};

  const seoTitle = t('recipes:SEO.RECIPES_BY_TAG.TITLE', { tag: title });
  const seoDescription = t('recipes:SEO.RECIPES_BY_TAG.DESCRIPTION', { tag: title });

  return {
    seoProps: {
      title: seoTitle,
      description: seoDescription,
      noindex: false,
      openGraph: {
        url: getOpenGraphUrl(),
        description: seoDescription,
        images: images || DEFAULT_SEO_IMAGE
      }
    },
    breadcrumbs: getPageSeoBreadcrumbs(t, [
      { name: t('NAVIGATION.BLOG.RECIPES.DEFAULT'), slug: APP_ROUTES.RECIPES_LANDING }
    ])
  };
}

// Recipes list
export function getRecipesListPageSeo(t: TFunction, recipesPage: IRecipesPage | undefined, urlParams: IRecipesListPageUrlParams): PageSeoProps {
  if (!recipesPage) return { seoProps: {}, breadcrumbs: [] };
  const {
    pagination,
    seo,
  } = recipesPage || {};

  const {
    count
  } = pagination || {};

  const {
    images
  } = seo || {};

  const {
    indexable
  } = urlParams;

  const seoTitle = count < 20
    ? t('recipes:SEO.RECIPES_LIST.TITLE_SINGULAR', { count })
    : t('recipes:SEO.RECIPES_LIST.TITLE_PLURAL', { count });

  const seoDescription = t('recipes:SEO.RECIPES_LIST.DESCRIPTION');

  return {
    seoProps: {
      title: seoTitle,
      description: seoDescription,
      openGraph: {
        description: seoDescription,
        images: images || DEFAULT_SEO_IMAGE,
        url: getOpenGraphUrl(),
      },
      noindex: !indexable,
      canonical: getPaginationCanonical(urlParams)
    },
    breadcrumbs: getPageSeoBreadcrumbs(t, [
      { name: t('NAVIGATION.BLOG.RECIPES.DEFAULT'), slug: APP_ROUTES.RECIPES_LANDING }
    ])
  };
}

// Recipe details

export function getRecipeDetailsPageSeo(t: TFunction, recipe?: IRecipe): PageSeoProps {
  if (!recipe) return { seoProps: {}, breadcrumbs: [] };
  const { title, description, images } = recipe?.seo || {};
  return {
    seoProps: {
      title,
      description,
      noindex: false,
      openGraph: {
        description,
        url: getOpenGraphUrl(),
        images: images || DEFAULT_SEO_IMAGE
      }
    },
    breadcrumbs: getPageSeoBreadcrumbs(t, [
      { name: t('NAVIGATION.BLOG.RECIPES.DEFAULT'), slug: APP_ROUTES.RECIPES_LANDING }
    ])
  };
}

export function getRecipeJsonLd(recipe?: IRecipe): undefined | RecipeJsonLdProps {
  if (!recipe) return undefined;
  const {
    details: {
      title,
      date,
    },
    stats,
    media: {
      video,
    },
    ingredients,
    instructions: {
      steps
    },
    seo,
  } = recipe;

  // Props

  const {
    preparationTime,
    cookingTime,
    servings,
  } = stats || {};

  const {
    text: textIngredients = []
  } = ingredients || {};

  const {
    title: videoTitle,
    url: videoUrl,
  } = video || {};

  const {
    description: seoDescription,
    images: seoImages,
  } = seo || {};

  // Parsed Props

  const prepTime = preparationTime ? minutesToISODuration(preparationTime?.value) : null;
  const cookTime = cookingTime ? minutesToISODuration(cookingTime?.value) : null;
  const totalTime = preparationTime && cookingTime ? minutesToISODuration(preparationTime.value + cookingTime.value) : null;

  const { value: yields = null } = servings || {};

  const parsedIngredients = textIngredients?.map(({ text }) => text) || [];
  const parseInstructions = steps?.map((step, index) => ({
    name: `Pasul ${index + 1}`,
    text: step,
  })) || [];

  const images = seoImages.map(({ url: seoImageUrl }) => seoImageUrl);

  const youtubeId = getYoutubeVideoIdFromUrl(videoUrl);

  const videoThumbnailUrls = youtubeId
    ? Array(4).fill(null).map((e, index) => `https://img.youtube.com/vi/${youtubeId}/${index}.jpg`)
    : [];

  // Return

  return {
    name: title,
    authorName: ['Freshful'],
    description: seoDescription,
    //
    datePublished: date,
    //
    prepTime: prepTime || undefined,
    cookTime: cookTime || undefined,
    totalTime: totalTime || undefined,
    yields: String(yields),
    //
    ingredients: parsedIngredients,
    instructions: parseInstructions,
    //
    images,
    ...(
      !videoUrl
        ? {}
        : {
          video: {
            name: videoTitle,
            description: seoDescription,
            contentUrl: videoUrl,
            uploadDate: date,
            thumbnailUrls: videoThumbnailUrls,
            regionsAllowed: [],
          }
        }
    )
  };
}
